import { supabase } from '@/shared/supabase/client'
import { AppError } from '@/shared/lib/appError'
import type { Session } from '@supabase/supabase-js'

/** Renova o JWT se expira dentro desta janela (evita refresh em todo request). */
const REFRESH_WITHIN_SEC = 120

export type AccessTokenForEdgeResult =
  | { ok: true; token: string }
  | { ok: false; error: string }

/**
 * JWT para `Authorization: Bearer` em Edge Functions.
 * Renova só quando ausente ou perto do vencimento — padrão alinhado ao steel-app-au ask-dextro.
 */
export const getAccessTokenForEdge = async (): Promise<AccessTokenForEdgeResult> => {
  const refresh = async (): Promise<Session | null> => {
    const { data, error } = await supabase.auth.refreshSession()
    if (error || !data.session?.access_token) return null
    return data.session
  }

  const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
  let session = sessionData?.session ?? null

  if (!session?.access_token) {
    session = await refresh()
    if (!session?.access_token) {
      return {
        ok: false,
        error: sessionError?.message ?? 'Sessão expirada. Faça login novamente.',
      }
    }
  }

  const exp = session.expires_at
  if (typeof exp === 'number' && exp * 1000 < Date.now() + REFRESH_WITHIN_SEC * 1000) {
    const refreshed = await refresh()
    if (refreshed?.access_token) {
      return { ok: true, token: refreshed.access_token }
    }
  }

  return { ok: true, token: session.access_token }
}

/** Lança AppError 401 quando não há token válido. */
export const requireAccessTokenForEdge = async (): Promise<string> => {
  const auth = await getAccessTokenForEdge()
  if (!auth.ok) {
    throw new AppError(auth.error, { status: 401, code: 'UNAUTHORIZED' })
  }
  return auth.token
}
